import { PermissionGuard } from '@/components/PermissionGuard';
import { TableSkeleton } from '@/components/TableSkeleton';
import { Button } from '@/components/ui/button';
import { useSimCardsStock } from '@/hooks/useSimCards';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useOrders } from '@/hooks/useOrders';
import { useDebouncedValue } from '@mantine/hooks';
import { CreditCard, Package, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimDataTable } from './sim-stock/sim-data-table';

const defaultPageInfo = {
  nextPage: null,
  previousPage: null,
  count: 0,
  nbPage: 0,
  page: 1,
};

export function SimStockView() {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const [filters, setFilters] = useState<{ operator?: string[] }>({});
  const [localSearchText, setLocalSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValue(localSearchText, 500);

  const { data, isLoading, isFetching } = useSimCardsStock({
    page: pagination.page,
    limit: pagination.limit,
    filters: {
      status: ['free'],
      operator: filters.operator,
    },
    searchText: debouncedSearchText,
  });

  const simCards = data?.simCards || [];
  const pageInfo = data?.pageInfo || defaultPageInfo;

  const isStockEmpty =
    pageInfo.count === 0 &&
    !debouncedSearchText &&
    !filters.operator?.length;

  const { data: orders = [] } = useOrders();

  const pendingSimOrders = orders.filter(
    (order) =>
      order.status.toLowerCase() === 'pending' &&
      order.items.some((item) => item.type === 'SIM')
  );

  const handleFiltersChange = (newFilters: { operator?: string[] }) => {
    setFilters(newFilters);
    setPagination({ page: 1, limit: pagination.limit });
  };

  const handleSearchChange = (newSearchText: string) => {
    setLocalSearchText(newSearchText);
    setPagination({ page: 1, limit: pagination.limit });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Mon stock de SIM
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez votre stock de cartes SIM disponibles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGuard permission="write">
            <Button
              size="sm"
              onClick={() => navigate(`/o/${tenantSlug}/sim-stock/order`)}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              Commander des SIM
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <TableSkeleton columns={3} />
        ) : isStockEmpty ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
              <CreditCard className="h-7 w-7 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              Aucune carte SIM en stock
            </h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm">
              Commandez des cartes SIM en stock pour les attribuer plus tard à vos lignes
            </p>
            {pendingSimOrders.length > 0 && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 mt-4 max-w-sm">
                <Package className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">
                  {pendingSimOrders.length === 1
                    ? '1 commande de SIM est en cours de traitement.'
                    : `${pendingSimOrders.length} commandes de SIM sont en cours de traitement.`}
                </p>
              </div>
            )}
            <PermissionGuard permission="write">
              <Button
                onClick={() =>
                  navigate(`/o/${tenantSlug}/sim-stock/order`)
                }
                className="mt-6 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 shadow-sm hover:shadow-md transition-all"
              >
                <Plus className="h-4 w-4" />
                Commander des SIM
              </Button>
            </PermissionGuard>
          </div>
        ) : (
          <SimDataTable
            data={simCards}
            pagination={pagination}
            pageInfo={pageInfo}
            onPaginationChange={setPagination}
            onFiltersChange={handleFiltersChange}
            onSearchChange={handleSearchChange}
            currentFilters={filters}
            currentSearchText={localSearchText}
            isFetching={isFetching}
          />
        )}
      </div>
    </div>
  );
}
